import { useChatClass } from "../../theme";
import { Card } from "../Card";
import { CardProps } from "../Card/types";
import { CarouselButton } from "./CarouselButton";
import { useCarouselCardScroll } from "./hooks";
import { Container } from "./styled";

export interface CarouselProps {
  /** A list of props objects which will be passed to `<Card />` components. */
  cards: CardProps[];
  /** A scrollable container */
  scrollContainerRef?: React.RefObject<HTMLElement>;
}

interface CarouselComponent extends React.FC<CarouselProps> {
  Container: typeof Container;
}

/** A carousel of `<Card />` components that can be scrolled natively or with buttons.
 *
 * In order to scroll properly and show the control buttons,
 * `<Carousel />` must be wrapped in a scrollable container. Also, an ancestor element
 * above the scrollable container should have a `position` property set to `relative`,
 * so that the control buttons can be positioned correctly.
 */
const Carousel: CarouselComponent = ({ cards, scrollContainerRef }) => {
  const { next, previous, showControls } =
    useCarouselCardScroll(scrollContainerRef);
  const chatClass = useChatClass();

  return (
    <Container className={chatClass("carousel")}>
      {cards.map((card, index) => (
        <Card {...card} key={index} />
      ))}
      {showControls && (
        <>
          <CarouselButton
            alignment="left"
            visible={previous.isButtonVisible}
            onClick={previous.scroll}
          />
          <CarouselButton
            alignment="right"
            visible={next.isButtonVisible}
            onClick={next.scroll}
          />
        </>
      )}
    </Container>
  );
};

Carousel.Container = Container;

export { Carousel };
